import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';

interface ServiceUploadHelpDialogProps {
  open: boolean;
  onClose: () => void;
}

export const ServiceUploadHelpDialog: React.FC<ServiceUploadHelpDialogProps> = ({
  open,
  onClose
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 2 
      }}>
        <Typography variant="h6">
          Service Upload File Specification
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        <Stack spacing={3}>
          {/* File Format Requirements */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              File Format Requirements
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Upload a CSV file containing service information. The file must include specific headers and follow the data format requirements below.
            </Alert>
          </Box>

          {/* Required Headers */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              CSV Headers (Required)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your CSV file must contain the following column headers in any order:
            </Typography>
            <Table size="small" sx={{ border: 1, borderColor: 'divider' }}>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Header Name</strong></TableCell>
                  <TableCell><strong>Required</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Example</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell><code>CenterId</code></TableCell>
                  <TableCell><Chip label="Required" color="error" size="small" /></TableCell>
                  <TableCell>Center identifier (without company prefix)</TableCell>
                  <TableCell>H01, H02, ASC01</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>Code</code></TableCell>
                  <TableCell><Chip label="Required" color="error" size="small" /></TableCell>
                  <TableCell>Unique service code/identifier</TableCell>
                  <TableCell>SRV001, LAB123, XRAY-01</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>Description</code></TableCell>
                  <TableCell><Chip label="Optional" color="success" size="small" /></TableCell>
                  <TableCell>Service description or name</TableCell>
                  <TableCell>Basic Lab Work, X-Ray Chest, MRI Brain</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>Company</code></TableCell>
                  <TableCell><Chip label="Optional" color="success" size="small" /></TableCell>
                  <TableCell>Company identifier (will use current if not provided)</TableCell>
                  <TableCell>PSN, PARSEC</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          {/* Example CSV */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              Example CSV Content
            </Typography>
            <Box sx={{ 
              bgcolor: 'grey.100', 
              p: 2, 
              borderRadius: 1, 
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              overflow: 'auto'
            }}>
              <pre style={{ margin: 0 }}>
{`CenterId,Code,Description,Company
H01,SRV001,Basic Lab Work,PSN
H01,XRAY-CHEST,X-Ray Chest,
H02,MRI-BRAIN,MRI Brain with Contrast,PARSEC`}
              </pre>
            </Box>
          </Box>

          {/* Important Notes */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              Important Notes
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Data Processing:</strong>
              </Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Empty rows are automatically skipped</li>
                <li>Leading and trailing spaces are automatically trimmed</li>
                <li>If Company is not provided, the current company context will be used</li>
                <li>CenterId will be prefixed with the company (e.g., "H01" becomes "PSN-H01")</li>
                <li>Existing services with the same Code will be updated</li>
                <li>New services will be created if the Code doesn't exist</li>
              </ul>
            </Alert>

            <Alert severity="error">
              <Typography variant="body2">
                <strong>Common Upload Errors:</strong>
              </Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Missing required headers (CenterId, Code)</li>
                <li>Empty CenterId or Code values</li>
                <li>Malformed CSV structure (extra commas, unclosed quotes)</li>
                <li>Invalid character encoding (use UTF-8)</li>
              </ul>
            </Alert>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceUploadHelpDialog;